import type { LocationStop } from '../SortableLocationRow'
import { CreateTaskInitialData, StopErrors } from './CreateTaskModal.types'

let stopIdCounter = 0

export function createStopId(): string {
  return `stop-${++stopIdCounter}`
}

export function createEmptyStop(): LocationStop {
  return {
    id: createStopId(),
    location: null,
    locationType: '',
    locationId: null,
    datetime: '',
  }
}

export function createInitialStops(initialData?: CreateTaskInitialData): LocationStop[] {
  const fromStop: LocationStop = {
    id: createStopId(),
    location: initialData?.fromLocation ?? null,
    locationType: initialData?.fromLocation?.type ?? '',
    locationId: (initialData?.fromLocation?.id as number) ?? null,
    datetime: initialData?.fromDatetime ?? '',
  }
  const toStop: LocationStop = {
    id: createStopId(),
    location: initialData?.toLocation ?? null,
    locationType: initialData?.toLocation?.type ?? '',
    locationId: (initialData?.toLocation?.id as number) ?? null,
    datetime: initialData?.toDatetime ?? '',
  }
  return [fromStop, toStop]
}

export function validateStops(stops: LocationStop[]): Map<string, StopErrors> {
  const errorMap = new Map<string, StopErrors>()
  const now = new Date()

  stops.forEach((stop, index) => {
    const errors: StopErrors = {}

    if (!stop.locationType) {
      errors.location = 'Location is required'
    }

    const isEndpoint = index === 0 || index === stops.length - 1

    if (!stop.datetime) {
      if (isEndpoint) {
        errors.datetime = 'Date/time is required'
      }
    } else {
      const date = new Date(stop.datetime)
      if (isNaN(date.getTime())) {
        errors.datetime = 'Invalid date/time'
      } else if (date < now) {
        errors.datetime = 'Date/time cannot be in the past'
      } else if (index > 0) {
        const prevStop = stops[index - 1]
        if (prevStop.datetime) {
          const prevDate = new Date(prevStop.datetime)
          if (!isNaN(prevDate.getTime()) && date <= prevDate) {
            errors.datetime = 'Must be later than the previous stop'
          }
        }
      }
    }

    if (index > 0 && !errors.location && isSameLocation(stop, stops[index - 1])) {
      errors.locationWarning = 'Location used subsequently'
    }

    if (errors.location || errors.datetime || errors.locationWarning) {
      errorMap.set(stop.id, errors)
    }
  })

  return errorMap
}

function isSameLocation(a: LocationStop, b: LocationStop): boolean {
  if (!a.location || !b.location) return false
  if (a.locationType === 'temporary' && b.locationType === 'temporary') {
    return a.location.address === b.location.address
  }
  return a.locationId !== null && a.locationId === b.locationId
}

export function checkConsecutiveDuplicates(
  stops: LocationStop[],
  existing: Map<string, StopErrors>,
): Map<string, StopErrors> {
  const next = new Map(existing)

  for (const [id, entry] of next) {
    if (entry.locationWarning) {
      const { locationWarning: _, ...rest } = entry
      if (Object.keys(rest).length === 0) {
        next.delete(id)
      } else {
        next.set(id, rest)
      }
    }
  }

  for (let i = 1; i < stops.length; i++) {
    if (isSameLocation(stops[i], stops[i - 1])) {
      const current = next.get(stops[i].id) ?? {}
      next.set(stops[i].id, { ...current, locationWarning: 'Location used subsequently' })
    }
  }

  return next
}
